import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { type FormikProps, withFormik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withAutosave, type AutosaveProps } from "utils/withAutosave";
import { type FeedbackPageFormFields, type FeedbackPageProps } from "./types";
import { bindAction } from "interfaces/redux";
import { bindActionCreators } from "redux";
import { useEffect, useRef, useState } from "react";
import { noop } from "lodash";
import { type FeedbackPagePayload } from "../types/models";
import { validationSchemas } from "utils";
import { campaignsStateSelector, feedbackPageFormFieldsSelector } from "../state/selectors";
import * as backgroundTasksActions from "../../../../BackgroundTasks/state/backgroundTasksActions";
import { saveFeedbackPage, setVideoIsUploading, setVideoUrl } from "../state/slices/feedbackPageSlice";
import FeedbackPageForm from "./Forms/FeedbackPageForm";
import { type VideoFeedbackPageTask } from "features/BackgroundTasks/taskPool";
import DataService from "features/Application/services/dataServices/typedDataService";
import fileUtils from "utils/fileUtils";
import { updateFeedbackPage } from "../state/thunks/campaignEntityStateThunk";

export type FeedbackPagePropsAll = FeedbackPageProps &
  FormikProps<FeedbackPageFormFields> &
  PropsFromRedux &
  AutosaveProps;

const FeedbackPage = (props: FeedbackPagePropsAll) => {
  const { setValues } = props;
  const values = useRef(props.values);
  useEffect(() => {
    values.current = props.values;
  }, [props.values]);

  const saveFeedbackPage = () => {
    props.saveFeedbackPage(values.current);
  };

  useEffect(() => {
    props.acceptHandlers?.({
      onNext: saveFeedbackPage,
      onPrevious: saveFeedbackPage,
    });
    props.subscribeOnDiscarded?.(props.resetForm);
    return () => {
      props.unSubscribeOnDiscarded?.(props.resetForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [videoUploadTaskId, setVideoUploadTaskId] = useState<string>("");

  const handleFileChange = (files: FileList) => {
    const newValues = {
      ...values.current,
      uploadedVideos: [fileUtils.toFileLike(files[0])],
    };
    setValues(newValues);
    props.saveFeedbackPage(newValues);
  };

  const uploadFilesHandler = async (files: FileList) => {
    props.setVideoIsUploading();
    const uploadedVideoUrl = await DataService.threatDefence.videoUploaderService.uploadFilesHandler(
      props.id,
      props.backgroundTasksActions,
      files,
      setVideoUploadTaskId,
    );
    props.setVideoUrl(uploadedVideoUrl);
  };

  const getTask = () => {
    return (props.tasks[videoUploadTaskId] as VideoFeedbackPageTask) || {};
  };

  const task = getTask();

  return (
    <FeedbackPageForm
      handleFileChange={handleFileChange}
      uploadFilesHandler={uploadFilesHandler}
      task={task}
      {...props}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    id: base.entityState.entityId,
    isCreating: false,
    feedbackPageFormFields: feedbackPageFormFieldsSelector(state),
    isLoaded: base.feedbackPage.isLoaded,
    isLoading: base.feedbackPage.isLoading,
    isDraft: base.details.campaign.isDraft,
    tasks: state.backgroundTasks.tasks,
    validation: base.campaignValidation,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateFeedbackPage: bindAction(updateFeedbackPage, dispatch),
  saveFeedbackPage: bindAction(saveFeedbackPage, dispatch),
  setVideoIsUploading: bindAction(setVideoIsUploading, dispatch),
  setVideoUrl: bindAction(setVideoUrl, dispatch),
  backgroundTasksActions: bindActionCreators(backgroundTasksActions, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<FeedbackPagePropsAll, FeedbackPageFormFields, FeedbackPagePayload>({
  getInitValues: (props) => props.feedbackPageFormFields,
  stateProvider: (values) => values,
  entityUpdater: (props) => props.updateFeedbackPage,
  isValid: (props) => props.isDraft && props.isValid && props.feedbackPageFormFields.videoUrl != "" && props.id > 0,
})(FeedbackPage);

/* istanbul ignore next */
const component = withFormik({
  validationSchema: validationSchemas.feedbackPage,
  enableReinitialize: true,
  mapPropsToValues: (props: FeedbackPageProps & PropsFromRedux): FeedbackPageFormFields => props.feedbackPageFormFields,
  validateOnMount: true,
  handleSubmit: noop,
})(withAutosaveComponent);

const ConnectedComponent = connector(component);
export default ConnectedComponent;
